import { Fragment, useEffect, useMemo, useState } from "react"

import { Tooltip, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useNavigate } from "react-router"
import { useLocation } from "react-router-dom"

import TourComponent from "../../Components/Tour"
import { FEATURE_FLAGS, isFeatureUnlocked } from "../../constants/feature-flags"
import { RoutesPaths } from "../../constants/user-routes"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import { navActions } from "../../Redux/nav/nav.slice"
import { isOnboardingFinished } from "../../Utils/onboarding/isOnboardingFinished"
import { highAccessRoles } from "../../Utils/permission"
import { buildTestId } from "../../Utils/testId"
import { getTourEl } from "../../Utils/utils"
import { getAlertsStatistics } from "../AlertsPage/redux/alerts.thunks"
import CreditsCreators from "../CreditsPage/reducer"
import { logoutThunk } from "../LoginPage/redux/auth.thunks"
import { getReceiptState } from "../ReceiptPage/redux/receipt.thunks"
import MenuComponent from "../User"
import { useDemoDataToggle } from "../User/hooks/useDemoDataToggle"
import {
  changeNextCreateAccountStep,
  setDemoTourStep,
  userSetProp
} from "../User/redux/user.actions"
import { userActions } from "../User/redux/user.slice"
import {
  getBusinessAccounts,
  getBusinessAccountsWithRole,
  getUserInfo,
  userSave
} from "../User/redux/user.thunks"
import actions from "./actions"
import Banners from "./components/Banners"
import { getDropdownFlagForPath, navIconsArr, NavLinkWithIcon } from "./navigation"
// import { sendExtensionMessage } from '../../Utils/extension';
import SidebarHeader from "./SidebarHeader"
import { TopSideBarMenu } from "./TopSidebarMenu"

import "./styles.less"

const { Text } = Typography

const HEALTH_GATE_TOOLTIP = "This section is temporarily unavailable"

const SidebarComponent = ({
  nav,
  isAdmin,
  changeSizeSidebar,
  openSidebar,
  userInfo,
  businessAccountsFetching,
  businessAccountDeleting,
  businessAccountsWithRole,
  businessAccounts,
  businessAccount,
  nextCreateAccountStep,
  demoTourStep,
  // demoRef,
  saveFetching,
  saveResult,
  alertsStatistic,
  creditsStatistic,
  receiptPrograms,
  balance,
  topMargin,
  featureFlag,
  ...props
}) => {
  const { isShowMenu, isShowSideBar, theme, isMobile, lastNotSettingUrl } = nav
  const closeMobileSidebar = () => props.setMobileMenuOpen(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const externalDemoMode = useExternalDemoMode()

  const [menu, setMenu] = useState("desktop")
  // Resolved before the first paint, so a page opened by URL renders with its
  // group already open instead of expanding it a frame later.
  const [activeKey, setActiveKey] = useState(() => {
    const flag = getDropdownFlagForPath(navIconsArr[menu], location.pathname)
    return flag ? [flag] : []
  })
  const activeKeyCalculated = useMemo(() => {
    if (userInfo?.is_demo && demoTourStep) return ["alerts"]
    return activeKey
  }, [activeKey, location, userInfo, demoTourStep])
  // const [showExtensionBtn, setShowExtensionBtn] = useState(false);

  const isActive = (path) => {
    return !location.pathname.includes(path)
  }

  // Mobile always renders the sidebar in its expanded form.
  const isSidebarExpanded = isMobile ? true : isShowSideBar

  const checkFeature = (flag) => flag && userInfo?.[flag] === false
  const onboardingFinished = isOnboardingFinished(userInfo)

  const menuIcons = useMemo(() => {
    // Only an explicit `true` opens a locked item: a merchant without the field
    // (or with it false) keeps the SOON placeholder.
    const isLocked = (menuItem) =>
      !!menuItem?.unlockFlag && !isFeatureUnlocked(userInfo, menuItem.unlockFlag)

    // A locked entry collapses into the same flat SOON placeholder the unreleased
    // pages use: keeping the dropdown would let the route-driven `activeKey`
    // expand it even though its header ignores clicks.
    const lock = (menuItem) => ({
      ...menuItem,
      markText: "SOON",
      markTextBordered: true,
      isNotClickable: true,
      disabledTooltip: null,
      dropdown: false,
      options: undefined,
      withoutUrl: true
    })

    const isVisible = (menuItem) =>
      !checkFeature(menuItem?.featureFlag) &&
      !(
        (onboardingFinished || userInfo?.is_created_by_invitation) &&
        menuItem?.url === "/getting_started"
      )

    const applyGates = (menuItem) => {
      if (menuItem?.url === RoutesPaths.health.overview) {
        const healthGates = {
          [RoutesPaths.health.overview]: userInfo?.is_business_health_overview_allowed,
          [RoutesPaths.health.monitoring]: userInfo?.is_business_health_allowed
        }
        const options = menuItem?.options?.map((option) =>
          option?.url in healthGates
            ? {
                ...option,
                isDisabled: !healthGates[option.url],
                disabledTooltip: HEALTH_GATE_TOOLTIP
              }
            : option
        )
        const allGated = options?.every((option) => option?.isDisabled)
        return {
          ...menuItem,
          options,
          ...(allGated ? { isNotClickable: true, disabledTooltip: null } : {})
        }
      }
      // CHA-11134 / §18.2: in Demo and External Demo, Configure (Digital
      // Receipt) is greyed out, cursor-not-allowed, with a tooltip — "All"
      // stays open so the mock dashboard is still reachable.
      if (menuItem?.url === RoutesPaths.receipts.all) {
        const configureDisabled = !!userInfo?.is_demo || externalDemoMode
        return {
          ...menuItem,
          options: menuItem?.options?.map((option) =>
            configureDisabled && option?.url?.startsWith(RoutesPaths.receipts._configure)
              ? {
                  ...option,
                  isDisabled: true,
                  disabledTooltip: "Configure isn't available in demo mode"
                }
              : option
          )
        }
      }
      return menuItem
    }

    // A group keeps its shape ({ group, items }); a bare item is mapped as
    // before. Groups whose items are all hidden drop out with their heading.
    return navIconsArr[menu]
      ?.map((entry) => {
        if (entry?.group) {
          const visible = entry.items?.filter(isVisible) ?? []
          if (!visible.length) return null
          // The heading greys out only when every item under it is locked —
          // a group with one open item still reads as available.
          return {
            ...entry,
            items: visible.map((item) => (isLocked(item) ? lock(item) : applyGates(item))),
            isLocked: visible.every(isLocked)
          }
        }
        if (!isVisible(entry)) return null
        return isLocked(entry) ? lock(entry) : applyGates(entry)
      })
      ?.filter(Boolean)
  }, [
    menu,
    onboardingFinished,
    userInfo?.is_created_by_invitation,
    userInfo?.is_business_health_allowed,
    userInfo?.[FEATURE_FLAGS.RECEIPTS],
    userInfo?.is_demo,
    externalDemoMode,
    userInfo?.is_business_health_overview_allowed
  ])

  const menuIconsBottom = navIconsArr["bottomMenu"]

  const siderBarStatistics = {
    alerts: alertsStatistic?.all,
    credits: creditsStatistic?.all,
    receipts: receiptPrograms?.find((p) => p?.program === "All")?.lookup_count
  }

  const hasHighAccessRole =
    businessAccountsWithRole?.some(({ role }) => highAccessRoles?.includes(role?.id)) ||
    businessAccountsWithRole.length === 0

  const isHighAccessRoleBA = !userInfo?.ba_role || highAccessRoles?.includes(userInfo?.ba_role?.id)

  // CHA1-2447: sidebar now fetches receipt state here to show an up-to-date
  // lookup count next to "Receipt → All". TODO: replace these 3 separate with 1
  // requests with a single sidebar-stats endpoint returning statistic
  const updateSidebarStatistics = () => {
    props.getAlertsStatistic()
    props.getCreditsStatistic()
    // ReceiptPage already fetches this itself while it's open, so skip here to avoid a duplicate request.
    if (!location.pathname.startsWith(RoutesPaths.receipts.all)) {
      props.getReceiptState()
    }
  }

  const { isShowDemoData } = useDemoDataToggle()

  useEffect(() => {
    if (onboardingFinished && saveResult) {
      updateSidebarStatistics()
    }
  }, [onboardingFinished, saveResult])

  useEffect(() => {
    updateSidebarStatistics()
  }, [isShowDemoData])

  useEffect(() => {
    if (isMobile && isShowMenu) {
      closeMobileSidebar()
    }
  }, [location.pathname])

  // Every navigation after the first: follow the address into its group. Keeping
  // the current array when the group is already open leaves a manual collapse of
  // some other group alone and costs no render.
  useEffect(() => {
    const flag = getDropdownFlagForPath(navIconsArr[menu], location.pathname)
    if (flag) setActiveKey((current) => (current.includes(flag) ? current : [flag]))
  }, [location.pathname, menu])

  const renderMenuItem = ({
    url,
    withoutUrl,
    iconDesk,
    iconNode,
    text,
    tooltipText,
    comingSoon,
    markText,
    markTextBordered,
    dropdown,
    options,
    flag,
    isNew,
    isNotClickable,
    isCustomAction,
    onClick,
    requireHighAccessRole,
    requireCurBAHighAccessRole,
    disabledTooltip,
    iconStyle,
    tourGuideId,
    featureFlag
  }) => {
    const isDisabled =
      isNotClickable ||
      (!hasHighAccessRole && requireHighAccessRole) ||
      (!isHighAccessRoleBA && requireCurBAHighAccessRole)
    const mappedOptions = options?.map((opt) => {
      const optDisabled =
        opt?.isDisabled ||
        (!hasHighAccessRole && opt?.requireHighAccessRole) ||
        (!isHighAccessRoleBA && opt?.requireCurBAHighAccessRole)
      return optDisabled
        ? { ...opt, isDisabled: true, disabledTooltip: opt?.disabledTooltip || disabledTooltip }
        : opt
    })
    const effectiveIsOpened = isSidebarExpanded
    // `flag` keys the dropdowns; plain items fall back to their label, with
    // `tooltipText` breaking the tie between the three "Analytics" entries.
    const itemTestId = buildTestId("sidebar-item", flag || tooltipText || text)
    const tooltip =
      (isDisabled && disabledTooltip) || (!effectiveIsOpened ? tooltipText || text : null)
    const navIcon = (
      <Tooltip
        placement={disabledTooltip && effectiveIsOpened ? "bottom" : "right"}
        title={tooltip}
        key={url}
        overlayClassName="sidebar-tooltip"
      >
        <div
          className={cls("nav-item-wrapper")}
          key={url}
          style={!effectiveIsOpened ? { margin: "0 auto", width: 36 } : {}}
        >
          <NavLinkWithIcon
            url={url}
            withoutUrl={withoutUrl || isDisabled}
            theme={theme}
            text={text}
            active={isActive(url)}
            iconType={iconDesk}
            isOpened={effectiveIsOpened}
            iconNode={iconNode}
            isSoon={comingSoon}
            isNew={isNew}
            markText={markText ? t(markText) : null}
            markTextBordered={markTextBordered}
            location={location.pathname}
            isDropdown={dropdown}
            options={mappedOptions}
            activeKey={activeKeyCalculated}
            setActiveKey={setActiveKey}
            flag={flag}
            t={t}
            isNotClickable={isDisabled}
            isCustomAction={isCustomAction}
            onClick={onClick}
            siderBarStatistics={siderBarStatistics}
            tourGuideId={tourGuideId}
            style={iconStyle}
            isMobile={isMobile}
            testId={itemTestId}
          />
        </div>
      </Tooltip>
    )
    return navIcon
  }

  // Groups render as a plain label (expanded only — the collapsed rail is icons
  // with no headings and no separators) followed by their items.
  const renderMenuIcons = (menu) =>
    menu.map((entry) =>
      entry?.group ? (
        <Fragment key={entry.group}>
          {isSidebarExpanded && (
            <div
              className={cls("main-sidebar_group-label", { disabled: entry.isLocked })}
              data-testid={buildTestId("sidebar-group", entry.group)}
            >
              {t(entry.group)}
            </div>
          )}
          {entry.items.map(renderMenuItem)}
        </Fragment>
      ) : (
        renderMenuItem(entry)
      )
    )

  // Demo tour, sidebar leg. Team, Integrations, Support and Help center left the
  // sidebar in CHA1-2847, so the tour now walks Configure → Integrate and the
  // account settings that are still there.
  const steps = useMemo(
    () => [
      {
        title: (
          <Text type="secondary">
            Connect with Stripe, Shopify, and more to keep everything in sync.
          </Text>
        ),
        target: () => getTourEl("configure-nav")
      },
      {
        title: (
          <Text type="secondary">Update your info, preferences, and security stuff here.</Text>
        ),
        target: () => getTourEl("account-settings"),
        nextButtonProps: {
          onClick: () => {
            navigate(RoutesPaths.dashboard.root)
            props.setDemoTourStep(6)
          },
          children: <text>Next</text>
        }
      }
    ],
    []
  )

  return (
    <>
      {isMobile && (
        <div
          className={cls("main-sidebar_shadow", { active: isShowMenu })}
          onClick={closeMobileSidebar}
        />
      )}
      <aside
        className={cls("main-sidebar", {
          "big-sidebar": isShowSideBar && !isMobile,
          "closed-sidebar": isMobile && !isShowMenu,
          "opened-sidebar": isMobile && isShowMenu
        })}
        style={{
          overflow: demoTourStep ? "hidden" : "auto",
          top: topMargin ?? 0,
          minHeight: topMargin ? `calc(100vh - ${topMargin}px)` : "100vh",
          maxHeight: topMargin ? `calc(100vh - ${topMargin}px)` : "100vh"
        }}
        id="main-sidebar"
      >
        <div className="main-sidebar_wrap">
          <SidebarHeader
            changeSizeSidebar={changeSizeSidebar}
            isShowSideBar={isShowSideBar}
            isMobile={isMobile}
            closeMobileSidebar={closeMobileSidebar}
          />
          {(onboardingFinished || userInfo?.is_created_by_invitation) && (
            <TopSideBarMenu
              changeNextCreateAccountStep={props.changeNextCreateAccountStep}
              changeBusinessAccount={props.changeBusinessAccount}
              getBusinessAccountsWithRole={props.getBusinessAccountsWithRole}
              getBusinessAccounts={props.getBusinessAccounts}
              getUser={props.getUser}
              userSaveResultReset={props.userSaveResultReset}
              setRedirectAfterCreateAccount={props.setRedirectAfterCreateAccount}
              isMobile={isMobile}
            />
          )}
          {/* <div className='get-started-block'>
              <Progress type="circle" percent={25} size={20} style={{transform: 'rotate(180deg)'}} />
              <Spin indicator={<LoadingOutlined spin />}/>
              <LoadingOutlined />
              {isShowSideBar && <Text type="secondary" style={{fontSize: 14}}>Getting started</Text>}
            </div> */}
          <div style={{ marginTop: "16px" }}></div>
          {renderMenuIcons(menuIcons)}
        </div>
        <div className="main-sidebar_bottom">
          {renderMenuIcons(menuIconsBottom)}
          {(isShowSideBar || isMobile) && <Banners />}
          {!isMobile && <MenuComponent type={"sidebar-bottom-menu"} />}

          {/* {props.children} */}
        </div>
        <TourComponent
          open={demoTourStep === 5}
          steps={steps}
          onClose={() => props.setDemoTourStep(null)}
        />
      </aside>
    </>
  )
}

const mapStateToProps = (state) => ({
  nav: state.nav,
  isAdmin: state.auth.isAdmin,
  userInfo: state.auth.userInfo,

  saveFetching: state.user.saveFetching,
  saveResult: state.user.saveResult,
  nextCreateAccountStep: state.user.nextCreateAccountStep,

  businessAccountsFetching: state.user.businessAccountsWithRoleFetching,
  businessAccountsWithRole: state.user.businessAccountsWithRole,
  businessAccounts: state.user.businessAccounts,
  businessAccount: state.user.businessAccount,
  alertsStatistic: state.alerts?.statistics,
  creditsStatistic: state.credits?.statistics,
  receiptPrograms: state.receipt?.programs,
  balance: state.balance.balance,
  demoTourStep: state.user.demoTourStep,
  isMobile: state.nav.isMobile
})

const mapDispatchToProps = (dispatch) => ({
  changeSizeSidebar: (value) =>
    dispatch(actions.changeSizeSidebar({ key: "isShowSideBar", value })),
  setMobileMenuOpen: (value) => dispatch(navActions.navSetProp({ key: "isShowMenu", value })),
  logout: () => dispatch(logoutThunk()),
  getBusinessAccounts: () => dispatch(getBusinessAccounts()),
  getBusinessAccountsWithRole: () => dispatch(getBusinessAccountsWithRole()),
  changeBusinessAccount: (newBusinessAccountId) =>
    dispatch(userSave({ current_business_account_id: newBusinessAccountId })),
  userSaveResultReset: () => dispatch(userActions.userSaveResultReset()),
  getUser: () => dispatch(getUserInfo()),
  changeNextCreateAccountStep: (nextCreateAccountStep) =>
    dispatch(changeNextCreateAccountStep(nextCreateAccountStep)),
  setRedirectAfterCreateAccount: (location) =>
    dispatch(userSetProp({ key: "redirectAfterCreateAccount", value: location })),
  setDemoTourStep: (step) => dispatch(setDemoTourStep(step)),
  getAlertsStatistic: () => dispatch(getAlertsStatistics()),
  getCreditsStatistic: () => dispatch(CreditsCreators.getCreditsStatisticsRequest()),
  getReceiptState: () => dispatch(getReceiptState())
})

export default connect(mapStateToProps, mapDispatchToProps)(SidebarComponent)
